import React from "react";
import PermissionsTableHeader from "./PermissionsTableHeader";
import PermissionsCategoryRow from "./PermissionsCategoryRow";
import PermissionsItemRow from "./PermissionsItemRow";
import AdminCheckbox from "../AdminCheckbox";
import { ScrollArea } from "../ui/scroll-area";
import LoadingState from "../LoadingState";

const PermissionsTable = ({
  permissions,
  loading,
  onPermissionChange,
  activeSubTab,
  formsContentType,
  setFormsContentType,
  isPermissionDisabled,
}) => {
  // Get permission types based on active tab
  const getPermissionTypes = () => {
    if (!permissions || !permissions.permissionTypes) {
      // Default fallback
      return ["search_view", "create", "edit", "delete", "print"];
    }
    return permissions.permissionTypes;
  };

  // Get permission labels based on active tab
  const getPermissionLabels = () => {
    const permissionTypes = getPermissionTypes();

    const labelMap = {
      search_view: "Search/View",
      create: "Create",
      edit: "Edit",
      delete: "Delete",
      print: "Print",
      view: "View",
      view_open: "View/Open",
      create_view: "Create/View",
      enabled: "Enabled",
    };

    return permissionTypes.map((type) => labelMap[type] || type);
  };

  if (loading) {
    return <LoadingState message="Loading Permissions..." />;
  }

  if (
    !permissions ||
    (!permissions.isFlat &&
      (!permissions.categories || permissions.categories.length === 0)) ||
    (permissions.isFlat &&
      (!permissions.items || permissions.items.length === 0))
  ) {
    console.log("No permissions data - showing empty state");
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-customBlue text-lg font-semibold mb-2">
            No permissions data available
          </div>
          <div className="text-gray-500">
            Please check your connection and try again
          </div>
        </div>
      </div>
    );
  }

  const permissionTypes = getPermissionTypes();
  const permissionLabels = getPermissionLabels();
  const isFormsContent = activeSubTab === "forms_content";

  return (
    <div className="h-full flex flex-col">
      {/* Forms Content Select Dropdown */}
      {activeSubTab === "forms_content" && (
        <div className="mb-4 flex items-center">
          <div className="flex items-center gap-3">
            <label
              htmlFor="content-type"
              className="text-sm font-medium text-gray-700 whitespace-nowrap"
            >
              Content Type:
            </label>
            <select
              id="content-type"
              value={formsContentType}
              onChange={(e) => setFormsContentType(e.target.value)}
              className="w-40 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
            >
              <option value="general">General</option>
              <option value="tabs">Tabs</option>
            </select>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0">
        <ScrollArea className="h-full">
          <table className="w-full border-collapse">
            <PermissionsTableHeader permissionLabels={permissionLabels} />
            <tbody>
              {/* Check if this is a flat structure (miscellaneous) */}
              {permissions.isFlat && permissions.items
                ? // Render flat items directly (for miscellaneous tab)
                  permissions.items.map((item, itemIndex) => {
                    return (
                      <PermissionsItemRow
                        key={item.id}
                        item={item}
                        categoryId={null} // No category for flat items
                        permissionTypes={permissionTypes}
                        onPermissionChange={onPermissionChange}
                        isFlat={permissions.isFlat}
                        isPermissionDisabled={isPermissionDisabled}
                        formsContentType={formsContentType}
                      />
                    );
                  })
                : // Render hierarchical structure (for all other tabs)
                  permissions.categories.map((category, index) => {
                    return (
                      <React.Fragment key={category.id}>
                        {/* Level 1: Category Row */}
                        <PermissionsCategoryRow
                          category={category}
                          permissionTypes={permissionTypes}
                          onPermissionChange={onPermissionChange}
                          isPermissionDisabled={isPermissionDisabled}
                          formsContentType={formsContentType}
                        />

                        {/* Level 2 & 3: Handle different structures based on tab */}
                        {category.items &&
                          category.items.length > 0 &&
                          category.items.map((item, itemIndex) => {
                            // For forms_content: Check if this is a parent group (like Applications)
                            if (
                              isFormsContent &&
                              item.isParentGroup &&
                              item.items &&
                              item.items.length > 0
                            ) {
                              // This is a 4-level structure (Category > Parent Group > Form > Fields)
                              return (
                                <React.Fragment key={item.id}>
                                  {/* Level 2: Parent Group Row (like Applications) */}
                                  <PermissionsParentGroupRow
                                    parentGroup={item}
                                    categoryId={category.id}
                                    permissionTypes={permissionTypes}
                                    onPermissionChange={onPermissionChange}
                                    isPermissionDisabled={isPermissionDisabled}
                                    formsContentType={formsContentType}
                                  />

                                  {/* Level 3 & 4: Forms and their Fields */}
                                  {item.items.map((form, formIndex) => (
                                    <React.Fragment key={form.id}>
                                      {/* Level 3: Form Row */}
                                      <PermissionsFormRow
                                        form={form}
                                        categoryId={category.id}
                                        parentGroupId={item.id}
                                        permissionTypes={permissionTypes}
                                        onPermissionChange={onPermissionChange}
                                        isPermissionDisabled={
                                          isPermissionDisabled
                                        }
                                        indentLevel={3}
                                        formsContentType={formsContentType}
                                      />

                                      {/* Level 4: Field/Tab Rows */}
                                      {form.items.map((field, fieldIndex) => (
                                        <PermissionsItemRow
                                          key={field.id}
                                          item={field}
                                          categoryId={category.id}
                                          parentGroupId={item.id}
                                          formId={form.id}
                                          permissionTypes={permissionTypes}
                                          onPermissionChange={
                                            onPermissionChange
                                          }
                                          indentLevel={4} // Deepest indentation
                                          isPermissionDisabled={
                                            isPermissionDisabled
                                          }
                                          formsContentType={formsContentType}
                                        />
                                      ))}
                                    </React.Fragment>
                                  ))}
                                </React.Fragment>
                              );
                            }
                            // For forms_content: Regular 3-level structure (no parent group)
                            else if (
                              isFormsContent &&
                              !item.isParentGroup &&
                              item.items &&
                              item.items.length > 0
                            ) {
                              return (
                                <React.Fragment key={item.id}>
                                  {/* Level 2: Form Row (intermediate level) */}
                                  <PermissionsFormRow
                                    form={item}
                                    categoryId={category.id}
                                    permissionTypes={permissionTypes}
                                    onPermissionChange={onPermissionChange}
                                    isPermissionDisabled={isPermissionDisabled}
                                    indentLevel={2}
                                    formsContentType={formsContentType}
                                  />

                                  {/* Level 3: Field/Tab Rows */}
                                  {item.items.map((field, fieldIndex) => (
                                    <PermissionsItemRow
                                      key={field.id}
                                      item={field}
                                      categoryId={category.id}
                                      formId={item.id} // Pass form ID for nested items
                                      permissionTypes={permissionTypes}
                                      onPermissionChange={onPermissionChange}
                                      indentLevel={3} // Standard deepest indentation
                                      isPermissionDisabled={
                                        isPermissionDisabled
                                      }
                                      formsContentType={formsContentType}
                                    />
                                  ))}
                                </React.Fragment>
                              );
                            }
                            // For forms: Check if item has children (like "Applications")
                            else if (
                              activeSubTab === "forms" &&
                              item.items &&
                              item.items.length > 0
                            ) {
                              return (
                                <React.Fragment key={item.id}>
                                  {/* Level 2: Parent Item Row (like "Applications") */}
                                  <PermissionsItemRow
                                    item={item}
                                    categoryId={category.id}
                                    permissionTypes={permissionTypes}
                                    onPermissionChange={onPermissionChange}
                                    indentLevel={2} // Parent level indentation
                                    isPermissionDisabled={isPermissionDisabled}
                                    isParentRow={true} // Mark as parent row for gray background
                                    formsContentType={formsContentType}
                                  />

                                  {/* Level 3: Child Item Rows */}
                                  {item.items.map((child, childIndex) => (
                                    <PermissionsItemRow
                                      key={child.id}
                                      item={child}
                                      categoryId={category.id}
                                      parentId={item.id} // Pass parent ID for nested items
                                      permissionTypes={permissionTypes}
                                      onPermissionChange={onPermissionChange}
                                      indentLevel={3} // Deepest indentation
                                      isPermissionDisabled={
                                        isPermissionDisabled
                                      }
                                      isParentRow={false} // Child rows use regular styling
                                      formsContentType={formsContentType}
                                    />
                                  ))}
                                </React.Fragment>
                              );
                            } else {
                              // For other tabs or regular items: standard two-level structure
                              return (
                                <PermissionsItemRow
                                  key={item.id}
                                  item={item}
                                  categoryId={category.id}
                                  permissionTypes={permissionTypes}
                                  onPermissionChange={onPermissionChange}
                                  indentLevel={2} // Standard indentation
                                  isPermissionDisabled={isPermissionDisabled}
                                  formsContentType={formsContentType}
                                />
                              );
                            }
                          })}
                      </React.Fragment>
                    );
                  })}
            </tbody>
          </table>
        </ScrollArea>
      </div>
    </div>
  );
};

// Parent Group Row for 4-level structure in forms_content (like Applications)
const PermissionsParentGroupRow = ({
  parentGroup,
  categoryId,
  permissionTypes,
  onPermissionChange,
  isPermissionDisabled,
  formsContentType,
}) => {
  return (
    <tr className="bg-gray-300 border-t border-b border-gray-200">
      <td className="py-2 pl-8 font-medium text-gray-800">
        {parentGroup.name}
      </td>
      {permissionTypes.map((permType) => {
        const disabled = isPermissionDisabled
          ? isPermissionDisabled(parentGroup.id, permType, categoryId)
          : false;

        return (
          <td key={permType} className="p-1">
            <div className="flex justify-center">
              <AdminCheckbox
                checked={parentGroup.permissions[permType] || false}
                onChange={(e) =>
                  onPermissionChange(
                    categoryId,
                    parentGroup.id,
                    permType,
                    e.target.checked,
                    { isParentGroup: true, contentType: formsContentType }
                  )
                }
                disabled={disabled}
              />
            </div>
          </td>
        );
      })}
    </tr>
  );
};

//Form Row for the intermediate level in forms_content
const PermissionsFormRow = ({
  form,
  categoryId,
  parentGroupId,
  permissionTypes,
  onPermissionChange,
  isPermissionDisabled,
  indentLevel = 2,
  formsContentType,
}) => {
  const getPaddingClass = () => {
    switch (indentLevel) {
      case 2:
        return "pl-8"; // Standard form indentation
      case 3:
        return "pl-16"; // Form under parent group (properly indented after Applications)
      default:
        return "pl-8";
    }
  };

  // Lighter gray for forms under parent groups
  const getBackgroundClass = () => {
    if (indentLevel === 3) {
      return "bg-gray-200"; // Lighter gray for forms under Applications
    }
    return "bg-gray-300"; // Standard gray for regular forms
  };

  return (
    <tr className={`${getBackgroundClass()} border-t border-b border-gray-200`}>
      <td className={`py-2 ${getPaddingClass()} font-medium text-gray-800`}>
        {form.name}
      </td>
      {permissionTypes.map((permType) => {
        const disabled = isPermissionDisabled
          ? isPermissionDisabled(form.id, permType, categoryId, parentGroupId)
          : false;

        return (
          <td key={permType} className="p-1">
            <div className="flex justify-center">
              <AdminCheckbox
                checked={form.permissions[permType] || false}
                onChange={(e) => {
                  if (parentGroupId) {
                    // 4-level structure
                    onPermissionChange(
                      categoryId,
                      form.id,
                      permType,
                      e.target.checked,
                      {
                        parentGroupId,
                        isFormLevelClick: true,
                        contentType: formsContentType,
                      }
                    );
                  } else {
                    // 3-level structure
                    onPermissionChange(
                      categoryId,
                      form.id,
                      permType,
                      e.target.checked,
                      {
                        isFormLevelClick: true,
                        formId: form.formId,
                        contentType: formsContentType,
                      }
                    );
                  }
                }}
                disabled={disabled}
              />
            </div>
          </td>
        );
      })}
    </tr>
  );
};

export default PermissionsTable;
